import * as moment from 'moment';
import { AfterViewInit, EventEmitter, Component, Input, OnInit, Output } from '@angular/core';
import { SiteAdvertsService } from '../../../core/services/data/site-adverts.service';
import { OrderService } from '../../../core/services/data/order.service';
import { LogsService } from '../../../core/services/logs.service';
import { UserService } from '../../../core/services/data/user.service';
import { CheckoutService } from '../../../core/services/checkout.service';

declare const paypal;

const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';
// const PROMO_TYPES = [PROMO_TYPE_PERCENT, PROMO_TYPE_AMOUNT];
const CURRENCY_USD = 'USD';
const PAYPAL = 'PayPal';

interface customWindow extends Window {
	Rewardful?: any;
	development: any;
}
declare const window: customWindow;

@Component({
	selector: 'app-paypal',
	templateUrl: './paypal.component.html',
	styleUrls: ['./paypal.component.scss'],
})
export class PaypalComponent implements AfterViewInit, OnInit {
	@Input() amount;

	@Input() cartPurchaseEnabled;

	@Input() order;

	@Input() promoCode;

	@Output() paypalResult = new EventEmitter();

	@Output() paymentStatus = new EventEmitter();

	private development;

	private payPalEnv;

	private author$;

	private author;

	constructor(
		private sas: SiteAdvertsService,
		private os: OrderService,
		private ls: LogsService,
		private us: UserService,
		private checkoutService: CheckoutService,
	) {
		this.development = window.development;
		this.payPalEnv = this.development ? 'sandbox' : 'production';
	}

	ngOnInit() {
		this.author$ = this.us.author$.subscribe(
			(data: any) => {
				this.author = data;
			},
		);
	}

	ngAfterViewInit() {
		this.tryInit();
	}

	tryInit() {
		if (typeof paypal !== 'undefined') {
			this.initPayPalButton();
		} else {
			setTimeout(() => {
				this.tryInit();
			}, 50);
		}
	}

	getDiscount(price, promoCode) {
		if (promoCode.discountType === PROMO_TYPE_PERCENT) {
			const discountPercentage = promoCode.value / 100;
			const discount = discountPercentage * price;
			return Number(discount.toFixed(2));
		}

		if (promoCode.discountType === PROMO_TYPE_AMOUNT) {
			return Number(promoCode.value.toFixed(2));
		}

		return 0;
	}

	/**
   * Used to parse the item details for an order (i.e. when cart purchase settings ARE enabled)
   */
	getCartItemsAndDiscounts() {
		const items = [];
		let totalDiscount = 0;
		// eslint-disable-next-line no-prototype-builtins
		if (this.order.hasOwnProperty('items') && this.order.items.length) {
			this.order.items.forEach((item) => {
				const { item: _item } = item;
				if (item.itemType === 'featuredBook') {
					const { featureItem, _totalDiscount } = this.handleFeatureItem(_item);
					items.push(featureItem);
					totalDiscount += _totalDiscount;
				}
				if (item.itemType === 'promoStack') {
					const { promoStackItem, _totalDiscount } = this.handlePromoStackItem(_item);
					items.push(promoStackItem);
					totalDiscount += _totalDiscount;
				}

				if (item.itemType === 'event') {
					const { eventItem } = this.handleEventItem(_item);
					items.push(eventItem);
				}
			});
		}
		return {
			items,
			itemDiscounts: totalDiscount > 0 ? { currency_code: CURRENCY_USD, value: totalDiscount.toFixed(2) } : null,
		};
	}

	handleFeatureItem(item) {
		let totalDiscount = 0;
		const { _site, _siteAdvert, pubDate, ASIN, _id, _promoCode = null } = item;
		const name = this._truncateName(`${_site.name} ${_siteAdvert.name} on ${pubDate} for ${ASIN}`);
		const featureItem = {
			name,
			sku: `${pubDate}|${ASIN}|${_site._id}|${_siteAdvert._id}|${_id}`,
			description: `${pubDate}|${ASIN}|${_site._id}|${_siteAdvert._id}|${_id}`,
			quantity: '1',
			unit_amount: {
				currency_code: CURRENCY_USD,
				value: _siteAdvert.price.toFixed(2),
			},
		};
		if (_promoCode) {
			// Track the discount amount for each order item
			totalDiscount = this.getDiscount(_siteAdvert.price, _promoCode);
		}

		if (item.paymentAmount !== _siteAdvert.price) {
			const discount = _siteAdvert.price - item.paymentAmount;
			if (discount && discount < totalDiscount) {
				totalDiscount = discount;
			}
		} else {
			totalDiscount = 0;
		}

		return { featureItem, _totalDiscount: totalDiscount };
	}

	handleEventItem(item) {
		const eventDate = moment(item.date).format('YYYYMMDD');
		return {
			eventItem: {
				name: this._truncateName(item.name),
				sku: `${this._truncateName(item.name)}|${item.eventId}|${item.attendeeId}`,
				description: `${item.name}|${eventDate}`,
				quantity: '1',
				unit_amount: {
					value: item.paymentAmount.toFixed(2),
					currency_code: CURRENCY_USD,
				},
			},
			_totalDiscount: 0, // No discounts for events currently
		};
	}

	handlePromoStackItem(item) {
		let totalDiscount = 0;
		const startDate = moment(item.startDate).format('YYYYMMDD');
		const promoStackItem = {
			name: this._truncateName(`${item._promoStack.name}`),
			sku: `${startDate}|${item._book.ASIN}|${item._promoStack._id}`,
			description: `${startDate}|${item._book.ASIN}|${item._promoStack._id}`,
			quantity: '1',
			unit_amount: {
				currency_code: CURRENCY_USD,
				value: item._promoStack.discountedPrice.toFixed(2),
			},
		};

		if (item._promoCode) {
			// Track the discount amount for each order item
			totalDiscount = this.getDiscount(item._promoStack.discountedPrice, item._promoCode);
		}

		if (item._promoStack.discountedPrice !== item.paymentAmount) {
			const discount = item._promoStack.discountedPrice - item.paymentAmount;
			if (discount && discount < totalDiscount) {
				totalDiscount = discount;
			}
		} else {
			totalDiscount = 0;
		}

		return { promoStackItem, _totalDiscount: totalDiscount };
	}

	_getPriceByItemType({ itemType, item }) {
		if (itemType === 'featuredBook') {
			return item._siteAdvert.price;
		}
		if (itemType === 'promoStack') {
			return item._promoStack.discountedPrice;
		}
		if (itemType === 'event') {
			return item.paymentAmount;
		}

		this.ls.postLog({
			type: PAYPAL,
			message: `Unknown item type: ${itemType} for item: ${JSON.stringify(item)}`,
		});
		return 0;
	}

	/**
   * Used to parse the transaction details for an order (i.e. when cart purchase settings ARE enabled)
   * @param {array} items
   * @param {object} itemDiscounts
   */
	getCartTransactionDetails(items, itemDiscounts = null) {
		const { _id, items: orderItems } = this.order;
		let subtotal = 0;

		orderItems.forEach((item) => {
			subtotal += this._getPriceByItemType(item);
		});

		const response = {
			amount: {
				value: this.amount.toFixed(2), // total after all discounts
				currency_code: CURRENCY_USD,
				breakdown: {
					item_total: {
						currency_code: CURRENCY_USD,
						value: subtotal.toFixed(2), // subtotal for all items
					},
					discount: itemDiscounts, // combined discounts for all items
				},
			},
			description: `Payment for ${orderItems.length} ${orderItems.length > 1 ? 'items' : 'item.'}`,
			custom_id: `OrderID_${_id}`,
			invoice_id: `${_id}`,
			items,
		};

		return response;
	}

	initPayPalButton() {
		const thisref = this;
		const transactionType = this.cartPurchaseEnabled ? 'order' : 'feature';
		const transactionId = this.order;
		paypal.Buttons({
			env: thisref.payPalEnv,
			style: {
				shape: 'pill',
			},
			commit: true,
			createOrder: (data, actions) => {
				this.ls.postLog({
					type: PAYPAL,
					message: `Transaction initiated for ${transactionType}: ${transactionId}`,
				});
				const { items, itemDiscounts } = this.getCartItemsAndDiscounts();
				const transaction = this.getCartTransactionDetails(items, itemDiscounts);

				return actions.order.create({
					intent: 'CAPTURE',
					purchase_units: [transaction],
				}).catch((err) => {
					let errMsg = err && err.name ? `${err.name}: ` : '';
					errMsg += err && err.message ? `${err.message}` : '';
					this.ls.postLog({
						type: PAYPAL,
						message: `Failed to create PayPal order for ${transactionType}: ${transactionId}. ${errMsg}`,
					});
					// Logging to rollbar to debug paypal issue 5/8/2023
					this.ls.postLog({
						message: 'Failed to create PayPal order',
						transactionType,
						transactionId,
						transaction,
					});
					throw err;
				});
			},
			onApprove: (data, actions) => {
				this.paymentStatus.emit({ loading: true });
				const thirtyDayError = this.os.checkOrderForThirtyDayViolation(this.order);
				if (thirtyDayError && thirtyDayError.errorText) {
					const errorMsg = thirtyDayError.errorText;
					this.ls.postLog({ type: PAYPAL, message: errorMsg });
					thisref.paypalResult.emit({
						status: 'error',
						type: 'paypal',
						error: errorMsg,
					});
				} else {
					this.os.checkOrderSiteInventory(this.order._id).subscribe((invData: any) => {
						if (invData.success && invData.invalidFeatures && invData.advertsWithInventoryConflict) {
							if (!invData.invalidPromoStacks.length && !invData.invalidFeatures.length
								&& !invData.advertsWithInventoryConflict.length) {
								return actions.order.capture().then(
									(payment) => {
										this.ls.postLog({
											type: PAYPAL,
											message: `Transaction completed for ${transactionType}: ${transactionId}`,
										});
										// complete payment
										thisref.paypalResult.emit({
											data: payment,
											status: 'success',
											type: 'paypal',
										});
									},
								).catch((err) => {
									const errMsg = this.extractPayPalError(err);
									this.ls.postLog({
										type: PAYPAL,
										message: `Failed to post transaction to PayPal for ${transactionType}: ${transactionId}. ${errMsg}`,
									});
									thisref.paypalResult.emit({
										status: 'error',
										type: 'paypal',
										error: 'Failed to post transaction to PayPal',
									});
								});
							}

							this.paymentStatus.emit({ loading: false });

							if (invData.advertsWithInventoryConflict.length) {
								this.ls.postLog({
									type: PAYPAL,
									message: 'Could not process purchase: Inventory conflict',
								});
								// There is an oversell conflict for a site advert
								return thisref.paypalResult.emit({
									status: 'error',
									type: 'advert_inventory_conflict',
									data: invData.advertsWithInventoryConflict,
								});
							}

							if (invData.invalidPromoStacks.length) {
								const invalidPromoStacks = this.order.items.filter(({ itemId }) => invData.invalidPromoStacks
									.find(({ orderPromoStackId }) => orderPromoStackId === itemId));

								const errorText = [`The promo stacks you selected are no longer available for the dates you selected.
									Please select a new date for your promo stack.`];

								const errorExtra = invalidPromoStacks.reduce((acc, { itemId, item: { _promoStack, startDate } }) => {
									const dateLabel = _promoStack.releaseDuration > 1 ? 'starting' : '';

									acc[itemId] = `${_promoStack.name} ${dateLabel} on ${moment(startDate, 'YYYYMMDD').format('MMMM Do, YYYY')}`;
									return acc;
								}, {});

								return thisref.paypalResult.emit({
									status: 'error',
									type: 'sold_out_promo_stack',
									data: invData.advertsWithInventoryConflict,
									errorText,
									errorExtra,
									soldOut: true,
								});
							}

							this.ls.postLog({ type: PAYPAL, message: 'Could not process purchase: Sold Out' });
							// There is an invalid feature
							return thisref.paypalResult.emit({
								status: 'error',
								type: 'sold_out_cart',
								data: invData.invalidFeatures,
							});
						}
						const errorMsg = invData.message || 'Something went wrong.';
						this.ls.postLog({ type: PAYPAL, message: `Error: ${errorMsg}` });
						return thisref.paypalResult.emit({
							status: 'error',
							type: 'paypal',
							error: errorMsg,
						});
					});
				}
			},
			onCancel: (data, actions) => {
				this.checkoutService.log(PAYPAL, this.order, 'cancelled', 'PayPal transaction cancelled');
				thisref.paypalResult.emit({
					status: 'cancelled',
					type: 'paypal',
				});
			},
			onError: (error) => {
				const parsedError = this.extractPayPalError(error);
				const _error = !parsedError || parsedError === '' ? 'Failed to pay with paypal' : parsedError;

				this.checkoutService.log(PAYPAL, this.order, 'error', _error);
				thisref.paypalResult.emit({
					status: 'error',
					type: 'paypal',
					error,
				});
			},
		}).render('#paypal-button');
	}

	extractPayPalError(err) {
		if (err) {
			// Default the error message to what we know is included in the response
			let errorMessage = err.name ? `${err.name}: ` : '';
			errorMessage += err.message ? `${err.message}` : '';
			// Base case where we return the found string or number (error code)
			if (typeof err === 'string' || typeof err === 'number') {
				errorMessage = `\n${err}`; // concat onto new line
			} else if (typeof err === 'object') {
				// For each key found in the object, recursively loop through to find string or number
				Object.keys(err).forEach((key) => {
					errorMessage += `${this.extractPayPalError(err[key])} `;
				});
			}
			return errorMessage;
		}
		return '';
	}

	_truncateName(str) {
		if (str.length > 127) {
			return `${str.substring(0, 124).trim()}...`;
		}

		return str;
	}
}
